<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>

    <div class="sl-card">

        <!-- title -->
        <div class="sl-card__title">
            {{ $t('card.order_delivery.title') }}
        </div>

        <!-- actions (track or download) -->
        <div v-if="hasBeenShipped" class="sl-card-buttons absolute right-4 top-4">

            <!-- tracking -->
            <a v-if="canTrack" :href="trackingUrl" target="_blank" @click.stop>
                <mdi-icon icon="mdiMapSearchOutline" class="sl-card-button text-primary-500" />
            </a>
            <mdi-icon
                v-else
                v-tippy="{
                    arrow: true,
                    theme: 'sl-light',
                    animation : 'fade'
                }"
                icon="mdiMapSearchOutline"
                class="sl-card-button sl-card-button--disabled"
                :content="trackingStatus"
            />

            <!-- Download -->
            <span v-if="downloading" class="loader ml-2"></span>

            <mdi-icon
                v-else-if="deliveryNote"
                icon="mdiFileMarkerOutline"
                class="sl-card-button text-primary-500"
                @click="download"
            />

        </div>

        <!-- key/value (separate lines) -->
        <div v-for="prop in logistics_data" :key="prop.parameterCode" class="sl-card-details__line">
            <div class="sl-card-details__line-label">
                {{ $t(`parameters.${prop.parameterCode}.label`) }}:
            </div>

            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="sl-card-details__line-value" v-html="$pf(prop.parameterValue, prop.parameterCode, 'value')"></div>
        </div>

        <!-- Delivery Note -->
        <div v-if="hasBeenShipped" class="sl-card-details__line">

            <div class="sl-card-details__line-label">
                {{ $t('order_table.details.section.delivery_note.reference') }}:
            </div>

            <div v-if="deliveryNote" class="sl-card-details__line-value">
                {{ deliveryNote.reference_externe || deliveryNote.reference }}

                <!-- <span>
                    |

                    <a
                        v-tippy="{ arrow: true, theme: 'sl-light', animation : 'fade' }"
                        :content="trackingStatus"
                        :href="canTrack ? trackingUrl : '#!'"
                        :target="canTrack ? '_blank' : '_self'"
                        :class="{ 'lsn-table__link--disabled': !canTrack }"
                        @click.stop
                    >
                        {{ t('invoicing.delivery_notes.track') }}
                    </a>

                    |

                    <lsn-loader-link
                        :label="$t('common.download')"
                        :is-loading="isFileDownloading"
                        @click.stop="downloadFile"
                    />
                </span>-->
            </div>

            <div v-else class="sl-card-details__line-value">
                &empty;
            </div>
        </div>

    </div>

</template>

<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>

import axios from '@/axios';
import moment from 'moment';
import ParametersCollection from '@/utils/ParametersCollection.js';

const TRACKING_URLS = {
    CIBLEX_FR:   'https://secure.extranet.ciblex.fr/extranet/client/corps.php?module=colis&colis=%s',
    CONIL:       null,
    DATALENS:    null,
    DHL:         'https://www.dhl.com/ch-en/home/tracking/tracking-express.html?submit=1&tracking-id=%s',
    GEN_OPTIQUE: null,
    GLS_FR:      null,
    JULIA:       null,
    L_MAX:       null,
    MICRO:       null,
    NACEX:       null,
    NO7LOG:      null,
    NSL:         null,
    OCS:         null,
    PCH:         'https://service.post.ch/EasyTrack/submitParcelData.do?formattedParcelCodes=%s',
    PIELLE:      null,
    PIERRE:      null,
    PROLENS:     null,
    THE_DX:      null,
    TMED_AT:     null,
    TMED_AT_EDI: null,
    TMED_DE:     null,
    TMED_DE_EDI: null,
    TNT:         null,
    TRUEBLUE:    null,
    VCI:         null,
};

export default
{
    name: 'OrderDeliveryCard',

    props:
    {
        order:
        {
            type:     Object,
            required: true,
        },
    },

    data()
    {
        return {
            deliveryNote: null,
            downloading:  false,
        };
    },

    computed:
    {
        cEntity()
        {
            return this.$store.state.account.cEntity;
        },

        logistics_data()
        {
            const protoCode = this.prototypeCode ?? this.prototypeCodeReference;
            const parametersCollection = new ParametersCollection(this.$store, this.order.logistics_data, protoCode);

            // Order values
            return parametersCollection.getAccessibleItems();
        },

        prototypeCode()
        {
            return this.order.article_data['PROTOTYPE_CODE'];
        },

        prototypeCodeReference()
        {
            return this.order.article_data['PROTOTYPE_CODE_REFERENCE'];
        },

        hasBeenShipped()
        {
            let hasBeenShipped = false;

            this.order.status?.forEach(status =>
            {
                if(status.code == 'SHIPPED')
                {
                    hasBeenShipped = true;
                    return;
                }
            });

            return hasBeenShipped;
        },

        trackingUrl()
        {
            // Ensure we have a tracking code
            const tracking = this.deliveryNote?.tracking;

            if(!tracking)
            {
                return null;
            }

            // Ensure we have a tracking URL
            if(!TRACKING_URLS[this.deliveryNote.fk_code_distributeur])
            {
                return null;
            }

            const urlFormat = TRACKING_URLS[this.deliveryNote.fk_code_distributeur];

            // Inject the tracking code into the URL and return it
            return urlFormat.replace('%s', tracking);
        },

        canTrack()
        {
            const threshold = moment().subtract(1, 'month');
            const deliveryNoteDate = moment(this.deliveryNote?.fermeture);

            return this.trackingUrl && deliveryNoteDate.isSameOrAfter(threshold, 'day');
        },

        trackingStatus()
        {
            if(!this.trackingUrl)
            {
                // No valid tracking code/URL is known
                return this.$t('invoicing.delivery_notes.no_tracking_url');
            }
            else if(!this.canTrack)
            {
                // The tracking code is older than 2 weeks
                return this.$t('invoicing.delivery_notes.tracking_expired');
            }
            else
            {
                // The tracking URL is known and recent; nothing to say :-)
                return '';
            }
        },
    },

    created()
    {
        this.fetchDeliveryNote();
    },

    methods:
    {
        fetchDeliveryNote()
        {
            if(this.order.reference)
            {
                const eid = this.cEntity.id;
                const orderReference = this.order.reference;

                axios.get(`/api/delivery-note/entity/${eid}/order/${orderReference}`)
                    .then(response =>
                    {
                        this.deliveryNote = response.data;
                    })
                    .catch(error =>
                    {
                        // todo: handle error
                    });
            }
        },

        download()
        {
            this.downloading = true;

            const eid = this.cEntity.id;
            const documentId = this.deliveryNote.id;
            const fileUrl = `${import.meta.env.VITE_API_LOCATION}/api/delivery-note/${eid}/${documentId}/pdf`;

            axios.get(fileUrl, {
                timeout:      5000,
                responseType: 'text',
            })
                .then(response =>
                {
                // Use an in-memory <a> tag to download the base64-encoded PDF file
                    const a    = document.createElement('a');
                    a.href     = 'data:application/octet-stream;base64,' + response.data;
                    a.target   = '_blank';
                    a.download = (this.deliveryNote.reference_externe || this.deliveryNote.reference) + '.pdf';
                    a.click();
                })
                .catch(error =>
                {
                    // Nothing
                })
                .then(() =>
                {
                    this.downloading = false;
                });
        },
    },
};

</script>

<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style lang="scss" scoped>


</style>
